<script setup lang="ts">
import ConfigurationItem from '@/core/dialogs/create-report/ConfigurationItem.vue';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import ToggleChip from '@/core/elements/ToggleChip.vue';
import type {
  IImageSelectOption,
  ISupportMeasureReportAvailableContent,
  ISupportMeasureReportConfiguration,
} from '@/interfaces';
import { useSupportMeasureReportConfigurationStore } from '@/stores/supportMeasureReportConfigurationStore';
import {
  ESupportMeasureReportCoverSheet,
  ESupportMeasureReportSignatory,
} from 'common/enum/support.measure.report.enum';
import type { ISupportMeasureReportOptions } from 'common/interface/support-measure-report.interface';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  onDownload: (options: ISupportMeasureReportOptions) => void;
}>();

const { t } = useI18n();
const dialogRef = useTemplateRef<InstanceType<typeof DialogBase>>('dialogRef');
const configurationStore = useSupportMeasureReportConfigurationStore();

const coverSheetOptions = computed<IImageSelectOption<ESupportMeasureReportCoverSheet>[]>(() => [
  {
    value: ESupportMeasureReportCoverSheet.SIMPLE,
    imageSrc: '/assets/support-measures/report-cover-simple-preview.png',
    label: t('supportMeasureEntry.report.coverSheetSimple'),
  },
  {
    value: ESupportMeasureReportCoverSheet.PLANT,
    imageSrc: '/assets/support-measures/report-cover-plant-preview.png',
    label: t('supportMeasureEntry.report.coverSheetPlant'),
  },
]);

const signatoryOptions = computed<{ value: ESupportMeasureReportSignatory; label: string }[]>(() => [
  { value: ESupportMeasureReportSignatory.TEACHER, label: t('reports.summarySheet.teacher') },
  { value: ESupportMeasureReportSignatory.PARENTS, label: t('supportMeasureEntry.report.parents') },
  { value: ESupportMeasureReportSignatory.SCHOOL_ADMINISTRATION, label: t('reports.summarySheet.schoolOwner') },
]);

const form = ref<ISupportMeasureReportConfiguration>({ ...configurationStore.selectedConfiguration });
const availableContent = ref<ISupportMeasureReportAvailableContent>({
  competenceDevelopment: true,
  completionNote: true,
  materials: true,
});

const canDownload = computed<boolean>(() => !form.value.includeSignatures || form.value.signatories.length > 0);

const setSignatory = (signatory: ESupportMeasureReportSignatory, selected: boolean): void => {
  const others = form.value.signatories.filter((selectedSignatory) => selectedSignatory !== signatory);
  form.value.signatories = selected ? [...others, signatory] : others;
};

const handleDownload = (): void => {
  if (!canDownload.value) return;
  configurationStore.setSelectedConfiguration(form.value);
  const { includeSignatures, signatories, ...options } = form.value;
  props.onDownload({ ...options, signatories: includeSignatures ? signatories : [] });
  dialogRef.value?.hide();
};

const show = async (content: ISupportMeasureReportAvailableContent): Promise<void> => {
  form.value = { ...configurationStore.selectedConfiguration };
  availableContent.value = content;
  await dialogRef.value?.show();
};

defineExpose({ show });
</script>

<template>
  <DialogBase
    ref="dialogRef"
    :title="$t('supportMeasureEntry.report.createReport')"
    content-width-style="w-[640px] max-md:!w-screen"
  >
    <div class="flex flex-col gap-md pb-lg">
      <p class="text-body">{{ $t('supportMeasureEntry.report.designQuestion') }}</p>

      <section class="flex flex-col gap-md rounded-group bg-surface-secondary p-lg">
        <p class="font-semibold">{{ $t('supportMeasureEntry.report.coverSheet') }}</p>
        <div class="flex flex-wrap gap-lg">
          <ConfigurationItem
            v-for="option in coverSheetOptions"
            :key="option.value"
            :is-selected="option.value === form.coverSheet"
            :image-src="option.imageSrc"
            :label="option.label"
            @click="form.coverSheet = option.value"
            @keydown.enter="form.coverSheet = option.value"
          />
        </div>
      </section>

      <section class="flex flex-col gap-smd rounded-group bg-surface-secondary p-lg">
        <div>
          <p class="font-semibold">{{ $t('supportMeasureEntry.report.content') }}</p>
          <p>{{ $t('supportMeasureEntry.report.contentHint') }}</p>
        </div>
        <CustomCheckbox v-if="availableContent.competenceDevelopment" v-model="form.includeCompetenceDevelopment">
          {{ $t('supportMeasureEntry.report.showCompetenceDevelopment') }}
        </CustomCheckbox>
        <CustomCheckbox v-if="availableContent.completionNote" v-model="form.includeCompletionNote">
          {{ $t('supportMeasureEntry.report.showCompletionNote') }}
        </CustomCheckbox>
        <CustomCheckbox v-if="availableContent.materials" v-model="form.includeMaterials">
          {{ $t('supportMeasureEntry.report.showMaterials') }}
        </CustomCheckbox>
      </section>

      <section class="flex flex-col gap-md rounded-group bg-surface-secondary p-lg">
        <CustomCheckbox v-model="form.includeSignatures">
          <span class="font-semibold">{{ $t('supportMeasureEntry.report.signatures') }}</span>
        </CustomCheckbox>
        <div v-if="form.includeSignatures" class="flex flex-wrap gap-sm">
          <ToggleChip
            v-for="option in signatoryOptions"
            :key="option.value"
            :model-value="form.signatories.includes(option.value)"
            @update:model-value="setSignatory(option.value, $event)"
          >
            {{ option.label }}
          </ToggleChip>
        </div>
      </section>
    </div>

    <template #footer>
      <CustomButton class="ml-auto" :disabled="!canDownload" @click="handleDownload">
        {{ $t('supportMeasureEntry.report.download') }}
      </CustomButton>
    </template>
  </DialogBase>
</template>
